import { togglePurchaseOffer } from 'actions/purchases/PurchasesActions';
import { navigateToStep } from 'actions/onlineBooking/actions/OnlineBookingFlowActions';
import clsx from 'clsx';
import { PoweredByCuddles } from 'components/Cuddles/PoweredBy/PoweredByCuddles';
import { LeftColumn } from 'components/Layout/TwoColumns/LeftColumn/LeftColumn';
import { RightColumn } from 'components/Layout/TwoColumns/RightColumn/RightColumn';
import { TwoColumnsLayout } from 'components/Layout/TwoColumns/TwoColumnsLayout';
import { MarketplaceInfo } from 'components/Marketplace/Info/MarketplaceInfo';
import { OnlineBookingHeader } from 'components/OnlineBooking/Header/OnlineBookingHeader';
import { PurchaseCart } from 'components/OnlineBooking/Purchase/Cart/PurchaseCart';
import { PurchaseFooter } from 'components/OnlineBooking/Purchase/Footer/PurchaseFooter';
import { PurchaseOfferCard } from 'components/OnlineBooking/Purchase/OfferCard/PurchaseOfferCard';
import { PetChip } from 'components/Pet/Chip/PetChip';
import { useMarketplace } from 'hooks/useMarketplace';
import { useOnlineBookingState } from 'hooks/useOnlineBookingState';
import React from 'react';
import { useSelector } from 'react-redux';
import { getPurchaseCart, getPurchaseCartHasItems } from 'reducers/purchases/PurchasesSelectors';
import { dispatch } from 'store';
import { OnlineBookingStep } from 'types/OnlineBooking';
import { getPurchaseOffersByKind, PurchaseOfferKind, PurchaseOfferKinds } from 'types/Purchase';
import classes from './OnlineBookingPurchaseStep.module.scss';

const TABS: Array<{ label: string; value: PurchaseOfferKind }> = [
    { label: 'Packages', value: PurchaseOfferKinds.Package },
    { label: 'Memberships', value: PurchaseOfferKinds.Membership }
];

export const OnlineBookingPurchaseStep: React.FC = () => {
    const marketplace = useMarketplace();
    const { selectedPets } = useOnlineBookingState();
    const cart = useSelector(getPurchaseCart);
    const cartHasItems = useSelector(getPurchaseCartHasItems);

    const [selectedPetUuid, setSelectedPetUuid] = React.useState(selectedPets[0]?.uuid);
    const [selectedTab, setSelectedTab] = React.useState<PurchaseOfferKind>(
        PurchaseOfferKinds.Package
    );

    const selectedPetIndex = selectedPets.findIndex(pet => pet.uuid === selectedPetUuid);
    const isLastPet = selectedPetIndex === selectedPets.length - 1;
    const selectedPetOfferIds = (selectedPetUuid && cart[selectedPetUuid]) || [];
    const offers = getPurchaseOffersByKind(selectedTab);

    // The design asks for something for the pet on screen; the last pet may skip if the cart already has items
    const canContinue = selectedPetOfferIds.length > 0 || (isLastPet && cartHasItems);

    const backHandler = () => {
        dispatch(navigateToStep(OnlineBookingStep.ServiceCategory));
    };

    const submitHandler = () => {
        if (!isLastPet) {
            const nextPetUuid = selectedPets[selectedPetIndex + 1].uuid;

            document
                .getElementById(nextPetUuid)
                ?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });

            setSelectedPetUuid(nextPetUuid);
            return;
        }

        dispatch(navigateToStep(OnlineBookingStep.PurchaseCheckout));
    };

    return (
        <TwoColumnsLayout header={<OnlineBookingHeader onBack={backHandler} />}>
            <LeftColumn contentClassName="flex flex-col flex-1 gap-5">
                <div className={classes.petTabs}>
                    {selectedPets.map(pet => (
                        <PetChip
                            id={pet.uuid}
                            key={pet.uuid}
                            petName={pet.name}
                            petType={pet.type}
                            active={pet.uuid === selectedPetUuid}
                            checked={pet.uuid !== selectedPetUuid && !!cart[pet.uuid]?.length}
                            onClick={() => setSelectedPetUuid(pet.uuid)}
                        />
                    ))}
                </div>

                <div className={classes.tabs}>
                    {TABS.map(tab => (
                        <button
                            key={tab.value}
                            type="button"
                            className={clsx(classes.tab, {
                                [classes.tabActive]: tab.value === selectedTab
                            })}
                            onClick={() => setSelectedTab(tab.value)}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                <h1 className={classes.title}>
                    {selectedTab === PurchaseOfferKinds.Package
                        ? 'Select your Packages'
                        : 'Select your Memberships'}
                </h1>

                <div className="flex flex-col gap-4">
                    {offers.map(offer => (
                        <PurchaseOfferCard
                            key={offer.id}
                            offer={offer}
                            selected={selectedPetOfferIds.includes(offer.id)}
                            onToggle={() =>
                                selectedPetUuid &&
                                dispatch(togglePurchaseOffer(selectedPetUuid, offer.id))
                            }
                        />
                    ))}
                </div>

                <div className="mt-auto hide-on-mobile-and-tablet pt-1">
                    <PoweredByCuddles />
                </div>
            </LeftColumn>

            <RightColumn
                poweredByCuddles
                footer={<PurchaseFooter disabled={!canContinue} onSubmit={submitHandler} />}
            >
                <div className="flex flex-col gap-5 hide-on-mobile-and-tablet">
                    <MarketplaceInfo inverted marketplace={marketplace} />

                    <PurchaseCart activePetUuid={selectedPetUuid} />
                </div>
            </RightColumn>
        </TwoColumnsLayout>
    );
};
